import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/model.pb';
import { Field_Enum, Operator_Enum } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/service.pb';
import dayjs from 'dayjs';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { SchedulingOutcomeV2Enum, type CallRecord } from '@frontend/api-call-intel';
import { StatusChip } from '@frontend/appointment-status';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { SkeletonLoader, Text } from '@frontend/design-system';
import { useCallIntelDefaultsShallowStore } from '../../hooks/use-call-intel-defaults';
import { callIntelUtils } from '../../utils/call-intel-utils';
import { CallIntelChip } from '../chip/call-intel-chip';
import { IconWithText } from '../icon-with-text';
import { ContactUserName } from '../user-name/contact-user-name';

type AppointmentCardProps = {
  appointment: Appointment;
  isLastVisit?: boolean;
  variant: 'upcoming' | 'past';
};

type AppointmentSectionProps = {
  appointments: Appointment[];
  label: string;
  markLastVisit?: boolean;
  variant: 'upcoming' | 'past';
};

export type PatientTooltipProps = {
  call: Pick<CallRecord, 'locationId' | 'appointmentMetadata'>;
};

export const buildAppointmentLabels = ({
  appointment,
  minLabel = 'Min',
  variant,
}: {
  appointment: Appointment;
  minLabel?: string;
  variant: 'past' | 'upcoming';
}): { dateLabel: string; timeLabel: string } => {
  const start = appointment?.appointmentAt ? dayjs(appointment.appointmentAt) : null;
  const dateLabel = start ? start.format('MMM D, YYYY') : '';

  const durationMin: number | undefined = +appointment?.length;

  const startLabel = start ? start.format('h:mm A') : '';
  const endLabel = start ? start.add(durationMin, 'minute').format('h:mm A') : '';

  const timeLabel =
    variant === 'upcoming' ? `${startLabel} - ${endLabel}` : `${startLabel} (${durationMin} ${minLabel})`;

  return { dateLabel, timeLabel };
};

const AppointmentsLoader = () => {
  const { t } = useTranslation('analytics');
  return (
    <div css={styles.appointmentWrapper}>
      <Text size='medium' color='subdued'>
        {t('Appointments')}
      </Text>
      <div css={styles.skeletonLoaderWrapper}>
        <SkeletonLoader height={12} width='60%' />
        <SkeletonLoader height={12} width='40%' />
      </div>
    </div>
  );
};

const AppointmentCard = ({ appointment, isLastVisit = false, variant }: AppointmentCardProps) => {
  const { t } = useTranslation('analytics');

  const { dateLabel, timeLabel } = buildAppointmentLabels({
    appointment,
    minLabel: t('Min'),
    variant,
  });

  return (
    <>
      {variant === 'upcoming' ? (
        <div css={styles.upcomingAppointmentCard}>
          <div className='appointment-details'>
            <Text size='small' weight='bold'>
              {dateLabel}
            </Text>
            <Text size='small'>{timeLabel}</Text>
          </div>
        </div>
      ) : (
        <div css={styles.pastAppointmentCard(isLastVisit)}>
          {isLastVisit && (
            <div css={styles.lastVisitWrapper}>
              <Text size='small'>{t('Last Visit')}</Text>
            </div>
          )}
          <div className='appointment-details'>
            <div className='date-details'>
              <IconWithText
                icon='calendar-small'
                text={appointment?.appointmentAt ? dayjs(appointment?.appointmentAt).format('MMM D, YYYY') : ''}
              />
              <StatusChip className='status-indicator' status={appointment?.status} isIconOnly />
            </div>
            <IconWithText icon='clock-small' text={timeLabel} />
            <IconWithText text={appointment.types[0]} />
          </div>
        </div>
      )}
    </>
  );
};

const AppointmentSection = ({ appointments, label, markLastVisit = false, variant }: AppointmentSectionProps) => {
  if (!appointments?.length) return null;

  return (
    <div css={styles.appointmentWrapper}>
      <Text size='medium' color='subdued'>
        {label}
      </Text>

      <div css={styles.sectionList}>
        {appointments.map((data, idx) => (
          <AppointmentCard
            key={data.appointmentId}
            appointment={data}
            isLastVisit={markLastVisit && idx === 0}
            variant={variant}
          />
        ))}
      </div>
    </div>
  );
};

export const PatientTooltip = ({ call }: PatientTooltipProps) => {
  const { t } = useTranslation('analytics');
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  const { data, isLoading } = AppointmentsApiQueries.useListAppointments(
    {
      locationId: call.locationId ?? '',
      filters: (call.appointmentMetadata?.map((am) => am.appointmentId).filter(Boolean) as string[] | undefined)?.map(
        (appointmentId) => ({
          field: Field_Enum.APPOINTMENT_ID,
          operator: Operator_Enum.IN,
          value: appointmentId,
        })
      ),
    },
    { enabled: !!call.appointmentMetadata?.length && !!call.locationId }
  );

  const [pastAppointments, upcomingAppointments] = useMemo(() => {
    const past = [] as Appointment[];
    const upcoming = [] as Appointment[];

    data?.appointments?.forEach((appointment: Appointment) => {
      const appointmentDate = dayjs(appointment.appointmentAt);
      if (appointmentDate.isBefore(dayjs(), 'minute')) {
        past.push(appointment);
      } else {
        upcoming.push(appointment);
      }
    });

    return [
      // Sort past by most recent to oldest
      past.sort((a, b) => dayjs(b.appointmentAt).diff(dayjs(a.appointmentAt))),
      // Sort upcoming by soonest to furthest in the future
      upcoming.sort((a, b) => dayjs(a.appointmentAt).diff(dayjs(b.appointmentAt))),
    ];
  }, [data]);

  const appointmentCount = call?.appointmentMetadata?.length || 0;
  const hasAppointments = pastAppointments.length > 0 || upcomingAppointments.length > 0;

  const getStatusMessage = () => {
    if (isLoading) {
      return t('We found appointments created after this call that could be related.');
    }
    if (appointmentCount > 1) {
      return t('We found {{count}} appointments created after this call that could be related.', {
        count: appointmentCount,
      });
    }
    return t('We found an appointment created after this call that could be related.');
  };

  return (
    <>
      <div css={styles.header}>
        <Divider className='divider' />
        <ContactUserName
          contact={{
            ...callIntelUtils.getContactInfo(call),
            defaultName: t('Unknown'),
          }}
          locationId={call.locationId ?? ''}
        />
      </div>
      <div css={styles.body}>
        <div css={styles.content}>
          <div css={styles.schedulingContentWrapper}>
            <div css={styles.unscheduledChipWrapper}>
              <CallIntelChip
                opacity={0.5}
                as='schedulingOutcome'
                chipVariants={chipVariants}
                dataKey={SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED}
                label={
                  dataLabels.schedulingOutcomeV2?.[SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED] ??
                  SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED
                }
                key={SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED}
                strikeThrough
                strikeThroughConfig={{ color: theme.colors.warning80 }}
              />

              <Icon name='arrow-right-small' />
            </div>

            <CallIntelChip
              as='schedulingOutcome'
              chipVariants={chipVariants}
              dataKey={SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL}
              label={
                dataLabels.schedulingOutcomeV2?.[SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL] ??
                SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL
              }
              key={SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL}
            />

            <Text size='medium' weight='regular'>
              {getStatusMessage()}
            </Text>
          </div>

          <Divider css={styles.divider} />

          {isLoading ? (
            <AppointmentsLoader />
          ) : hasAppointments ? (
            <>
              <AppointmentSection
                appointments={upcomingAppointments}
                label={t('Upcoming Appointments')}
                variant='upcoming'
              />
              <AppointmentSection appointments={pastAppointments} label={t('Past Appointments')} variant='past' />
            </>
          ) : (
            <Text size='small' color='subdued'>
              {t('No related appointments found.')}
            </Text>
          )}
        </div>
      </div>
    </>
  );
};

const styles = {
  header: css`
    padding: ${theme.spacing(2)};
    background-color: ${theme.colors.white};
    border-top-left-radius: ${theme.borderRadius.large};
    border-top-right-radius: ${theme.borderRadius.large};

    figure {
      min-height: ${theme.spacing(4)};
      min-width: ${theme.spacing(4)};
      height: ${theme.spacing(4)};
      width: ${theme.spacing(4)};
    }
  `,
  body: css`
    padding: ${theme.spacing(1)};
  `,
  content: css`
    padding: ${theme.spacing(1)};
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.medium};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    box-shadow: ${theme.shadows.light};
  `,
  schedulingContentWrapper: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    align-items: flex-start;
  `,
  unscheduledChipWrapper: css`
    display: flex;
    align-items: center;
    width: 100%;
  `,
  divider: css`
    margin: ${theme.spacing(0)};
  `,
  appointmentWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  sectionList: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  lastVisitWrapper: css`
    background-color: ${theme.colors.secondary.eggplant10};
    border-top-left-radius: 7px;
    border-top-right-radius: 7px;
    padding: ${theme.spacing(0.5, 1)};
    p {
      color: ${theme.colors.secondary.eggplant70};
    }
  `,
  upcomingAppointmentCard: css`
    display: flex;
    box-shadow: ${theme.shadows.light};
    border-radius: ${theme.borderRadius.small};

    .status-indicator {
      background-color: ${theme.colors.warning50};
      width: ${theme.spacing(1)};
      height: auto;
      border-top-left-radius: ${theme.borderRadius.small};
      border-bottom-left-radius: ${theme.borderRadius.small};
    }
    .appointment-details {
      padding: ${theme.spacing(0.5, 1)};
    }
  `,
  pastAppointmentCard: (isLastVisit: boolean) => css`
    display: flex;
    flex-direction: column;
    border: 1px solid ${theme.colors.neutral10};
    border-radius: ${theme.borderRadius.medium};
    padding: ${!isLastVisit ? theme.spacing(1.5, 1) : 0};

    .date-details {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .appointment-details {
      padding: ${!isLastVisit ? theme.spacing(0) : theme.spacing(1)};
      gap: ${theme.spacing(0.5)};
      display: flex;
      flex-direction: column;
    }

    .status-indicator {
      width: ${theme.spacing(2.5)};
      height: ${theme.spacing(2)};
    }
  `,
  skeletonLoaderWrapper: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(1)};
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
    border-radius: ${theme.borderRadius.small};
    gap: ${theme.spacing(0.5)};
  `,
  betaWrapper: css`
    .divider {
      margin: ${theme.spacing(1.5, 0)};
    }

    .text {
      font-style: italic;
    }

    .beta-content {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
    }
  `,
};
